import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Empty, StatusBadge } from "../components/ui";
import { Check, CheckCheck, Filter, RefreshCw } from "lucide-react";

const MODULE_ROUTE = {
  legal: "/legal", legal_permits: "/legal", periodic_reports: "/legal",
  audits: "/audits", racs: "/audits", findings: "/audits", toa_cards: "/audits",
  studies: "/studies", wells: "/resources", well_payments: "/resources",
  resources: "/resources", waste: "/waste", esg: "/esg", brigades: "/brigades",
  procedures: "/iso14001", inspections: "/iso14001", iso_objectives: "/iso14001",
  advc_records: "/advc", smart_projects: "/smart", providers: "/providers",
  inductions: "/communication", map_areas: "/map",
};

const SEVERITIES = ["", "Crítica", "Alta", "Media"];
const MODULES = ["", "legal", "wells", "studies", "audits", "racs"];

export default function NotificationsInbox() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ severity: "", module: "", read: "", date_from: "", date_to: "" });
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v));
    try {
      const [inbox, count] = await Promise.all([
        api.get("/notifications/inbox", { params }),
        api.get("/notifications/inbox/unread-count"),
      ]);
      setItems(inbox.data);
      setUnread(count.data.unread);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function markRead(id) {
    await api.post(`/notifications/inbox/${id}/read`);
    load();
  }
  async function markAllRead() {
    await api.post("/notifications/inbox/mark-all-read");
    load();
  }
  async function runCheck() {
    await api.post("/notifications/check");
    load();
  }

  return (
    <div data-testid="notifications-inbox">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="card-h1" style={{ margin: 0 }}>Bandeja de Notificaciones</h1>
          <div className="text-xs text-dim mt-2">
            <span className="badge badge-red mr-2">{unread} no leídas</span>
            <span>{items.length} totales</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm" onClick={runCheck} data-testid="run-check-btn"><RefreshCw size={14} /> Ejecutar verificación</button>
          <button className="btn btn-sm" onClick={markAllRead} data-testid="mark-all-read"><CheckCheck size={14} /> Marcar todas como leídas</button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-title"><Filter size={14} /> Filtros</div>
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="label">Severidad</label>
            <select className="select" value={filter.severity} onChange={(e) => setFilter({ ...filter, severity: e.target.value })} data-testid="filter-severity">
              <option value="">Todas</option>
              {SEVERITIES.slice(1).map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Módulo</label>
            <select className="select" value={filter.module} onChange={(e) => setFilter({ ...filter, module: e.target.value })} data-testid="filter-module">
              <option value="">Todos</option>
              <option value="legal">Requisitos Legales</option>
              <option value="wells">Pozos</option>
              <option value="studies">Estudios Normativos</option>
              <option value="audits">Auditorías</option>
              <option value="racs">RACs</option>
            </select>
          </div>
          <div>
            <label className="label">Status</label>
            <select className="select" value={filter.read} onChange={(e) => setFilter({ ...filter, read: e.target.value })} data-testid="filter-read">
              <option value="">Todas</option>
              <option value="false">No leídas</option>
              <option value="true">Leídas</option>
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1"><label className="label">Desde</label><input type="date" className="input" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })} /></div>
            <div className="flex-1"><label className="label">Hasta</label><input type="date" className="input" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })} /></div>
          </div>
        </div>
      </div>

      <div className="table-wrap">
        {loading ? <div className="p-4 text-dim">Cargando...</div> : !items.length ? <Empty title="Sin notificaciones" sub="Cuando haya vencimientos próximos aparecerán aquí." /> : (
          <table className="table">
            <thead><tr><th>Fecha</th><th>Tipo</th><th>Título</th><th>Vencimiento</th><th>Días</th><th>Severidad</th><th>Email</th><th style={{ textAlign: "right" }}>Status</th></tr></thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} style={{ background: n.read ? "transparent" : "rgba(231,76,60,0.04)", cursor: "pointer" }}
                  onClick={async () => {
                    if (!n.read) await api.post(`/notifications/inbox/${n.id}/read`).catch(() => {});
                    const route = MODULE_ROUTE[n.module] || "/dashboard";
                    navigate(n.item_id ? `${route}?focus=${encodeURIComponent(n.item_id)}` : route);
                  }}
                  data-testid={`notif-row-${n.id}`}
                  title="Ir al registro de origen"
                >
                  <td className="text-xs">{new Date(n.created_at).toLocaleString("es-MX")}</td>
                  <td className="text-xs">{n.kind}</td>
                  <td><b style={{ fontWeight: n.read ? 400 : 600 }}>{n.title}</b></td>
                  <td className="text-xs">{n.due_date}</td>
                  <td>
                    <span className={`badge ${n.days_left < 0 ? "badge-red" : n.days_left <= 7 ? "badge-yellow" : "badge-green"}`}>
                      {n.days_left < 0 ? `${Math.abs(n.days_left)}d atrás` : `${n.days_left}d`}
                    </span>
                  </td>
                  <td><StatusBadge value={n.severity || "Media"} /></td>
                  <td className="text-xs">{n.email_sent ? "✉ Enviado" : "—"}</td>
                  <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "right" }}>
                    {n.read ? <span className="text-xs text-dim">Leída</span> : (
                      <button className="btn btn-sm" onClick={() => markRead(n.id)} data-testid={`mark-read-${n.id}`}><Check size={12} /> Marcar leída</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
